import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import {
  useBulkInventoryBinRecords,
  useProcessBulkInventoryBins,
  useRevertReturnToShelf,
} from '@/hooks';
import { EditServiceTypeModal } from '@/components/bulk-inventory/EditServiceTypeModal';
import type { ProcessBulkInventoryBinsResponse } from '@/services/propertyBook/BulkInventoryBinService';
import { LoadingSkeleton } from '@/components/shared/LoadingSkeleton';
import {
  TargetLocationSelectionModal,
  type ReturnBinTarget,
  type ReturnToShelfTargetGroup,
} from '@/components/bulk-inventory/TargetLocationSelectionModal';
import { IssueCartListSection } from '@/components/iem/workflows/issue/IssueCartListSection';
import { WorkflowCartRow } from '@/components/shared/workflows/WorkflowCartRow';
import { getErrorMessage } from '@/utils/httpClient';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { logger } from '@equip/utils/logger';

const log = logger.child({ component: 'BulkInventoryBinPage' });

type GroupKey = string;

type GroupedBin = {
  key: GroupKey;
  assetName: string;
  assetLin: string | null;
  assetNsn: string | null;
  assetCatalogId: string | null;
  catalogRecoverable: string | null;
  catalogSize: string | null;
  serviceTypeSample: string | null;
  recordIds: string[];
  stagedRecordIds: string[];
  serviceableCount: number;
  nonServiceableCount: number;
};

type GroupSelection = {
  action: 'return' | 'dispose' | 'mixed';
  quantity: number;
};

type SelectionClearScope = 'all' | GroupKey[];

type PendingProcessingRequest = {
  returnBinIds: string[];
  disposeBinIds: string[];
  clearSelectionScope: SelectionClearScope;
};

type GridRow = {
  id: GroupKey;
  assetName: string;
  assetLin: string;
  assetNsn: string;
  binQty: number;
  stagedQty: number;
  serviceableCount: number;
  nonServiceableCount: number;
  recoverable: string;
  catalogSize: string;
  serviceType: string;
  returnedQty: number;
};

type CartItem = {
  groupKey: GroupKey;
  assetName: string;
  assetLin: string | null;
  assetNsn: string | null;
  quantity: number;
  serviceSummary: string;
  recoverableLabel: string;
};

type PreparedProcessingRequest =
  | {
      ok: true;
      returnBinIds: string[];
      disposeBinIds: string[];
      autoTargets: ReturnBinTarget[];
      missingTargetGroups: ReturnToShelfTargetGroup[];
    }
  | {
      ok: false;
      error: string;
    };

interface CartSectionProps {
  title: string;
  items: CartItem[];
  onRemove: (groupKey: GroupKey) => void;
}

const buildGroupKey = (catalogId?: string | null): GroupKey => `${catalogId?.trim() || '—'}`;

const formatRecoverable = (
  value: string | null | undefined,
): { label: string; color: 'default' | 'success' | 'warning' } => {
  if (!value) {
    return { label: '—', color: 'default' };
  }

  const normalized = value.trim().toUpperCase();
  if (normalized.includes('NON')) {
    return { label: 'Non-Recoverable', color: 'warning' };
  }
  if (normalized.includes('RECOVERABLE')) {
    return { label: 'Recoverable', color: 'success' };
  }
  return { label: value, color: 'default' };
};

const formatServiceType = (value: string | null | undefined): string => {
  if (!value) {
    return 'Unknown';
  }
  return value.replace(/([a-z])([A-Z])/g, '$1 $2');
};

const getServiceSummary = (group: GroupedBin): string => {
  const hasServiceable = group.serviceableCount > 0;
  const hasNonServiceable = group.nonServiceableCount > 0;

  if (hasServiceable && hasNonServiceable) {
    return 'Mixed Serviceability';
  }
  if (hasServiceable) {
    return 'Serviceable';
  }
  if (hasNonServiceable) {
    return 'Non-Serviceable';
  }
  return formatServiceType(group.serviceTypeSample);
};

const getNextDisposeQuantity = ({
  currentSelection,
  requestedQuantity,
  maxQuantity,
}: {
  currentSelection?: GroupSelection;
  requestedQuantity: number;
  maxQuantity: number;
}): number => {
  const normalizedRequested = Math.max(0, Math.min(maxQuantity, requestedQuantity));

  if (currentSelection?.action === 'dispose' && currentSelection.quantity > 0) {
    return Math.min(maxQuantity, currentSelection.quantity + 1);
  }

  return normalizedRequested;
};

const CartSection: React.FC<CartSectionProps> = ({ title, items, onRemove }) => (
  <IssueCartListSection
    title={title}
    items={items}
    renderItem={(item) => {
      const recoverableLabel = item.recoverableLabel;
      const subtitleParts = [
        item.assetLin ? `LIN ${item.assetLin}` : null,
        item.assetNsn ? `NSN ${item.assetNsn}` : null,
        item.serviceSummary !== 'Unknown' ? item.serviceSummary : null,
        recoverableLabel !== '—' ? recoverableLabel : null,
        `Qty ${item.quantity}`,
      ].filter(Boolean);

      return (
        <WorkflowCartRow
          key={item.groupKey}
          title={item.assetName}
          subtitle={subtitleParts.join(' • ') || undefined}
          actions={
            <Stack direction="row" spacing={0.5}>
              <IconButton
                size="small"
                color="primary"
                aria-label={`Remove ${item.assetName} from ${title}`}
                onClick={() => onRemove(item.groupKey)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Stack>
          }
        />
      );
    }}
  />
);

export const BulkInventoryBinPage: React.FC = () => {
  const isViewer = useWriteGuard();
  const { data = [], isLoading, isFetching, error } = useBulkInventoryBinRecords();
  const processMutation = useProcessBulkInventoryBins();
  const revertMutation = useRevertReturnToShelf();

  const {
    mutateAsync: processBins,
    isPending: isProcessing,
    reset: resetProcessBins,
  } = processMutation;

  const [isTargetLocationModalOpen, setIsTargetLocationModalOpen] = useState(false);
  const [targetLocationGroups, setTargetLocationGroups] = useState<ReturnToShelfTargetGroup[]>([]);
  const [pendingProcessingRequest, setPendingProcessingRequest] =
    useState<PendingProcessingRequest | null>(null);

  const [selectionByGroup, setSelectionByGroup] = useState<Record<GroupKey, GroupSelection>>({});
  const [inputQtyByGroup, setInputQtyByGroup] = useState<Record<GroupKey, number>>({});
  const [submissionResult, setSubmissionResult] = useState<ProcessBulkInventoryBinsResponse | null>(
    null,
  );
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  // Edit Service Type modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalGroup, setEditModalGroup] = useState<GroupedBin | null>(null);

  // Clear All confirmation dialog state
  const [clearAllConfirmOpen, setClearAllConfirmOpen] = useState(false);
  const [clearSuccessMessage, setClearSuccessMessage] = useState<string | null>(null);

  // Cancel Return to Shelf confirmation state
  const [revertConfirmGroupKey, setRevertConfirmGroupKey] = useState<GroupKey | null>(null);

  const groups = useMemo<GroupedBin[]>(() => {
    if (!data.length) {
      return [];
    }

    const grouped = new Map<GroupKey, GroupedBin>();

    data.forEach((record) => {
      const isStaged = record.workflowStatus === 'ready_to_shelf';
      const key = buildGroupKey(record.assetCatalogId);
      const normalized = String(record.serviceType ?? '')
        .trim()
        .toLowerCase();
      const isServiceable = normalized === 'serviceable';
      const isNonServiceable = normalized === 'nonserviceable' || normalized === 'non-serviceable';
      const serviceSample = typeof record.serviceType === 'string' ? record.serviceType : null;

      const existing = grouped.get(key);
      if (!existing) {
        grouped.set(key, {
          key,
          assetName: record.assetName,
          assetLin: record.assetLin ?? null,
          assetNsn: record.assetNsn ?? null,
          assetCatalogId: record.assetCatalogId ?? null,
          catalogRecoverable: record.catalogRecoverable ?? null,
          catalogSize: record.catalogSize ?? null,
          serviceTypeSample: serviceSample,
          recordIds: isStaged ? [] : [record.id],
          stagedRecordIds: isStaged ? [record.id] : [],
          serviceableCount: !isStaged && isServiceable ? 1 : 0,
          nonServiceableCount: !isStaged && isNonServiceable ? 1 : 0,
        });
        return;
      }

      if (isStaged) {
        existing.stagedRecordIds.push(record.id);
      } else {
        existing.recordIds.push(record.id);
        if (isServiceable) {
          existing.serviceableCount += 1;
        }
        if (isNonServiceable) {
          existing.nonServiceableCount += 1;
        }
      }
      if (!existing.serviceTypeSample && serviceSample) {
        existing.serviceTypeSample = serviceSample;
      }
      if (!existing.catalogRecoverable && record.catalogRecoverable) {
        existing.catalogRecoverable = record.catalogRecoverable;
      }
      if (!existing.catalogSize && record.catalogSize) {
        existing.catalogSize = record.catalogSize;
      }
      if (!existing.assetLin && record.assetLin) {
        existing.assetLin = record.assetLin;
      }
      if (!existing.assetNsn && record.assetNsn) {
        existing.assetNsn = record.assetNsn;
      }
    });

    return Array.from(grouped.values());
  }, [data]);

  const groupsByKey = useMemo(() => new Map(groups.map((group) => [group.key, group])), [groups]);

  useEffect(() => {
    setSelectionByGroup((previous) => {
      let changed = false;
      const next: Record<GroupKey, GroupSelection> = {};

      (Object.entries(previous) as Array<[GroupKey, GroupSelection]>).forEach(([key, value]) => {
        const group = groupsByKey.get(key);
        if (!group) {
          changed = true;
          return;
        }
        const maxForAction = group.recordIds.length;
        const clampedQty = Math.max(0, Math.min(maxForAction, value.quantity));
        if (clampedQty <= 0) {
          changed = true;
          return;
        }
        next[key] = clampedQty === value.quantity ? value : { ...value, quantity: clampedQty };
        if (clampedQty !== value.quantity) {
          changed = true;
        }
      });

      return changed ? next : previous;
    });
  }, [groupsByKey]);

  useEffect(() => {
    setInputQtyByGroup((previous) => {
      let changed = false;
      const next: Record<GroupKey, number> = {};

      (Object.entries(previous) as Array<[GroupKey, number]>).forEach(([key, value]) => {
        const group = groupsByKey.get(key);
        if (!group) {
          changed = true;
          return;
        }
        const maxForInput = group.recordIds.length;
        const clamped = Math.max(0, Math.min(maxForInput, value));
        if (clamped <= 0) {
          changed = true;
          return;
        }
        next[key] = clamped;
        if (clamped !== value) {
          changed = true;
        }
      });

      return changed ? next : previous;
    });
  }, [groupsByKey, selectionByGroup]);

  const clearFeedback = useCallback(() => {
    setSubmissionResult(null);
    setSubmissionError(null);
    setClearSuccessMessage(null);
    resetProcessBins();
  }, [resetProcessBins]);

  const clearSelections = useCallback((clearSelectionScope: SelectionClearScope) => {
    if (clearSelectionScope === 'all') {
      setSelectionByGroup({});
      setInputQtyByGroup({});
      return;
    }

    if (clearSelectionScope.length === 0) {
      return;
    }

    setSelectionByGroup((previous) => {
      let changed = false;
      const next = { ...previous };

      clearSelectionScope.forEach((groupKey) => {
        if (groupKey in next) {
          delete next[groupKey];
          changed = true;
        }
      });

      return changed ? next : previous;
    });

    setInputQtyByGroup((previous) => {
      let changed = false;
      const next = { ...previous };

      clearSelectionScope.forEach((groupKey) => {
        if (groupKey in next) {
          delete next[groupKey];
          changed = true;
        }
      });

      return changed ? next : previous;
    });
  }, []);

  const submitProcessing = useCallback(
    async (
      payload: {
        returnBinIds: string[];
        disposeBinIds: string[];
        returnBinTargets?: ReturnBinTarget[];
      },
      clearSelectionScope: SelectionClearScope = 'all',
    ) => {
      const { returnBinIds, disposeBinIds, returnBinTargets } = payload;

      const result = await processBins({
        returnBinIds,
        disposeBinIds,
        returnBinTargets,
      });
      setSubmissionResult(result);
      clearSelections(clearSelectionScope);
    },
    [clearSelections, processBins],
  );

  const prepareProcessingRequest = useCallback(
    (requestedSelections: Record<GroupKey, GroupSelection>): PreparedProcessingRequest => {
      const missingGroup = Object.keys(requestedSelections).find((key) => !groupsByKey.has(key));
      if (missingGroup) {
        return {
          ok: false,
          error: 'Some selected bins are no longer available. Refresh and try again.',
        };
      }

      const expected = {
        return: 0,
        dispose: 0,
      };
      Object.entries(requestedSelections).forEach(([key, selection]) => {
        if (selection.action === 'mixed') {
          const group = groupsByKey.get(key);
          if (group) {
            expected.return += group.serviceableCount;
            expected.dispose += group.nonServiceableCount;
          }
        } else if (selection.action === 'return') {
          expected.return += selection.quantity;
        } else {
          expected.dispose += selection.quantity;
        }
      });

      const usedIds = new Set<string>();
      const returnBinIds: string[] = [];
      const disposeBinIds: string[] = [];
      const recordsById = new Map(data.map((record) => [record.id, record]));

      (Object.entries(requestedSelections) as Array<[GroupKey, GroupSelection]>).forEach(
        ([key, selection]) => {
          if (selection.quantity <= 0) {
            return;
          }
          const group = groupsByKey.get(key);
          if (!group) {
            return;
          }
          const availableIds = group.recordIds.filter((id) => !usedIds.has(id));

          if (selection.action === 'mixed') {
            // Split bins by service type: serviceable → return, non-serviceable → dispose
            availableIds.forEach((id) => {
              const record = recordsById.get(id);
              const svcType = (record?.serviceType ?? '').toLowerCase();
              usedIds.add(id);
              if (svcType === 'serviceable') {
                returnBinIds.push(id);
              } else {
                disposeBinIds.push(id);
              }
            });
            return;
          }

          // Sort so bins matching the intended action's service type are selected first.
          // Return prefers Serviceable bins; Dispose prefers NonServiceable bins.
          const sorted = [...availableIds].sort((a, b) => {
            const aType = (recordsById.get(a)?.serviceType ?? '').toLowerCase();
            const bType = (recordsById.get(b)?.serviceType ?? '').toLowerCase();
            if (selection.action === 'return') {
              return (aType === 'serviceable' ? 0 : 1) - (bType === 'serviceable' ? 0 : 1);
            }
            return (aType === 'nonserviceable' ? 0 : 1) - (bType === 'nonserviceable' ? 0 : 1);
          });

          const selectedIds = sorted.slice(
            0,
            Math.min(selection.quantity, sorted.length),
          );
          selectedIds.forEach((id) => usedIds.add(id));
          if (selection.action === 'return') {
            returnBinIds.push(...selectedIds);
          } else {
            disposeBinIds.push(...selectedIds);
          }
        },
      );

      if (returnBinIds.length !== expected.return || disposeBinIds.length !== expected.dispose) {
        return {
          ok: false,
          error: 'Unable to allocate bins for selected quantities. Please review inputs.',
        };
      }

      const returnRecords = returnBinIds
        .map((binId) => recordsById.get(binId) ?? null)
        .filter((record): record is (typeof data)[number] => Boolean(record));

      const missingCatalogBinId = returnRecords.find((record) => !record.assetCatalogId)?.id;
      if (missingCatalogBinId) {
        return {
          ok: false,
          error: `Return bin ${missingCatalogBinId} is missing a catalog ID. Assign a catalog before returning to shelf.`,
        };
      }

      const missingLocationRecords = returnRecords.filter(
        (record) => !record.assetLocationId && !record.barcodeMappingLocationId,
      );

      const autoTargets = returnRecords
        .filter((record) => !record.assetLocationId && record.barcodeMappingLocationId)
        .map((record) => ({
          binId: record.id,
          targetLocationId: record.barcodeMappingLocationId as string,
        }));

      const groupedMissing = new Map<string, ReturnToShelfTargetGroup>();

      missingLocationRecords.forEach((record) => {
        const groupKey = buildGroupKey(record.assetCatalogId);
        const existing = groupedMissing.get(groupKey);
        if (!existing) {
          groupedMissing.set(groupKey, {
            groupKey,
            assetName: record.assetName,
            quantity: returnRecords.filter((r) => buildGroupKey(r.assetCatalogId) === groupKey)
              .length,
            missingBinIds: [record.id],
          });
          return;
        }
        existing.missingBinIds.push(record.id);
      });

      return {
        ok: true,
        returnBinIds,
        disposeBinIds,
        autoTargets,
        missingTargetGroups: Array.from(groupedMissing.values()),
      };
    },
    [data, groupsByKey],
  );

  const beginProcessing = useCallback(
    async (
      requestedSelections: Record<GroupKey, GroupSelection>,
      clearSelectionScope: SelectionClearScope = 'all',
    ) => {
      clearFeedback();
      setPendingProcessingRequest(null);

      const prepared = prepareProcessingRequest(requestedSelections);
      if (!prepared.ok) {
        setSubmissionResult(null);
        setSubmissionError(prepared.error);
        return;
      }

      const { returnBinIds, disposeBinIds, autoTargets, missingTargetGroups } = prepared;

      if (missingTargetGroups.length > 0) {
        setTargetLocationGroups(missingTargetGroups);
        setPendingProcessingRequest({
          returnBinIds,
          disposeBinIds,
          clearSelectionScope,
        });
        setIsTargetLocationModalOpen(true);
        return;
      }

      try {
        await submitProcessing(
          {
            returnBinIds,
            disposeBinIds,
            returnBinTargets: autoTargets.length > 0 ? autoTargets : undefined,
          },
          clearSelectionScope,
        );
      } catch (mutationError) {
        log.error('Failed to process bulk inventory bins', { error: String(mutationError) });
        setSubmissionResult(null);
        setSubmissionError(getErrorMessage(mutationError));
      }
    },
    [clearFeedback, prepareProcessingRequest, submitProcessing],
  );

  const handleOpenEditModal = useCallback((group: GroupedBin) => {
    setEditModalGroup(group);
    setEditModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false);
    setEditModalGroup(null);
  }, []);

  const handleProcessAll = useCallback(async () => {
    clearFeedback();

    if (!groups.length) {
      return;
    }

    const allSelections: Record<GroupKey, GroupSelection> = {};

    groups.forEach((group) => {
      if (group.recordIds.length === 0) {
        return;
      }
      const binQty = group.recordIds.length;
      if (!binQty) {
        return;
      }
      if (group.serviceableCount > 0 && group.nonServiceableCount === 0) {
        allSelections[group.key] = { action: 'return', quantity: group.serviceableCount };
        return;
      }
      if (group.nonServiceableCount > 0 && group.serviceableCount === 0) {
        allSelections[group.key] = { action: 'dispose', quantity: group.nonServiceableCount };
        return;
      }
      if (group.serviceableCount > 0 && group.nonServiceableCount > 0) {
        allSelections[group.key] = { action: 'mixed', quantity: group.recordIds.length };
        return;
      }
    });

    if (!Object.keys(allSelections).length) {
      return;
    }

    try {
      await beginProcessing(allSelections);
    } catch (error) {
      log.error('Failed to process all bins', { error: String(error) });
      setSubmissionError(getErrorMessage(error));
    }
  }, [beginProcessing, clearFeedback, groups]);

  const handleProcessReturnToShelf = useCallback(async () => {
    clearFeedback();

    if (!groups.length) {
      return;
    }

    const returnSelections: Record<GroupKey, GroupSelection> = {};

    groups.forEach((group) => {
      if (group.recordIds.length === 0) {
        return;
      }
      if (group.serviceableCount > 0) {
        returnSelections[group.key] = { action: 'return', quantity: group.serviceableCount };
      }
    });

    if (!Object.keys(returnSelections).length) {
      setSubmissionError('No serviceable items available to return to shelf.');
      return;
    }

    try {
      await beginProcessing(returnSelections);
    } catch (error) {
      log.error('Failed to process return to shelf', { error: String(error) });
      setSubmissionError(getErrorMessage(error));
    }
  }, [beginProcessing, clearFeedback, groups]);

  const hasItemsToProcess = useMemo(
    () => Object.values(selectionByGroup).some((selection) => selection.quantity > 0),
    [selectionByGroup],
  );

  const handleCartRemove = useCallback(
    (groupKey: GroupKey) => {
      clearFeedback();
      setSelectionByGroup((previous) => {
        if (!previous[groupKey]) {
          return previous;
        }
        const { [groupKey]: _removed, ...rest } = previous;
        return rest;
      });
      setInputQtyByGroup((previous) => {
        if (!(groupKey in previous)) {
          return previous;
        }
        const { [groupKey]: _removed, ...rest } = previous;
        return rest;
      });
    },
    [clearFeedback],
  );

  const handleComplete = useCallback(async () => {
    if (!hasItemsToProcess) {
      setSubmissionResult(null);
      setSubmissionError('Select at least one asset to process.');
      resetProcessBins();
      return;
    }

    await beginProcessing(selectionByGroup);
  }, [beginProcessing, hasItemsToProcess, resetProcessBins, selectionByGroup]);

  const handleCancelReturn = useCallback((groupKey: GroupKey) => {
    setRevertConfirmGroupKey(groupKey);
  }, []);

  const handleRevertConfirmed = useCallback(async () => {
    if (!revertConfirmGroupKey) {
      return;
    }
    const group = groupsByKey.get(revertConfirmGroupKey);
    setRevertConfirmGroupKey(null);
    if (!group || group.stagedRecordIds.length === 0) {
      return;
    }
    clearFeedback();
    try {
      await revertMutation.mutateAsync({ binIds: group.stagedRecordIds });
      setClearSuccessMessage(
        `Cancelled return for ${group.stagedRecordIds.length} item${group.stagedRecordIds.length === 1 ? '' : 's'}.`,
      );
    } catch (revertError) {
      log.error('Failed to revert return to shelf', { error: String(revertError) });
      setSubmissionError(getErrorMessage(revertError));
    }
  }, [clearFeedback, groupsByKey, revertConfirmGroupKey, revertMutation]);

  const handleClearAll = useCallback(() => {
    setClearAllConfirmOpen(true);
  }, []);

  const handleClearAllConfirmed = useCallback(() => {
    setClearAllConfirmOpen(false);
    clearFeedback();
    clearSelections('all');
    setClearSuccessMessage('All staged disposal items have been cleared.');
  }, [clearFeedback, clearSelections]);

  const handleProcessDisposal = useCallback(async () => {
    clearFeedback();
    const disposeSelections: Record<GroupKey, GroupSelection> = {};
    (Object.entries(selectionByGroup) as Array<[GroupKey, GroupSelection]>).forEach(([key, sel]) => {
      if (sel.action === 'dispose' && sel.quantity > 0) {
        disposeSelections[key] = sel;
      }
    });

    // When no manual selections exist, auto-select all non-serviceable-only groups
    if (!Object.keys(disposeSelections).length) {
      groups.forEach((group) => {
        if (group.recordIds.length === 0) {
          return;
        }
        if (group.nonServiceableCount > 0 && group.serviceableCount === 0) {
          disposeSelections[group.key] = { action: 'dispose', quantity: group.nonServiceableCount };
        }
      });
    }

    if (!Object.keys(disposeSelections).length) {
      setSubmissionError('No non-serviceable items available to process for disposal.');
      return;
    }
    const clearScope = Object.keys(disposeSelections) as GroupKey[];
    try {
      await beginProcessing(disposeSelections, clearScope);
    } catch (error) {
      log.error('Failed to process disposal', { error: String(error) });
      setSubmissionError(getErrorMessage(error));
    }
  }, [beginProcessing, clearFeedback, groups, selectionByGroup]);

  const handleTargetLocationCancel = useCallback(() => {
    setIsTargetLocationModalOpen(false);
    setTargetLocationGroups([]);
    setPendingProcessingRequest(null);
  }, []);

  const handleTargetLocationConfirm = useCallback(
    async (targets: ReturnBinTarget[]) => {
      const returnBinIds = pendingProcessingRequest?.returnBinIds ?? [];
      const disposeBinIds = pendingProcessingRequest?.disposeBinIds ?? [];
      const clearSelectionScope = pendingProcessingRequest?.clearSelectionScope ?? 'all';

      if (!returnBinIds.length) {
        handleTargetLocationCancel();
        return;
      }

      setIsTargetLocationModalOpen(false);

      // Build auto-targets for records that have barcode mapping but no asset location
      // (these weren't shown in the dialog since they have a known location)
      const recordsById = new Map(data.map((record) => [record.id, record]));
      const autoTargets: ReturnBinTarget[] = [];
      for (const binId of returnBinIds) {
        const record = recordsById.get(binId);
        if (record && !record.assetLocationId && record.barcodeMappingLocationId) {
          autoTargets.push({
            binId: record.id,
            targetLocationId: record.barcodeMappingLocationId,
          });
        }
      }

      // Combine user-selected targets with auto-targets
      const allTargets = [...targets, ...autoTargets];

      try {
        await submitProcessing(
          {
            returnBinIds,
            disposeBinIds,
            returnBinTargets: allTargets.length > 0 ? allTargets : undefined,
          },
          clearSelectionScope,
        );
      } catch (mutationError) {
        log.error('Failed to process bulk inventory bins (target locations)', {
          error: String(mutationError),
        });
        setSubmissionResult(null);
        setSubmissionError(getErrorMessage(mutationError));
      } finally {
        setTargetLocationGroups([]);
        setPendingProcessingRequest(null);
      }
    },
    [data, handleTargetLocationCancel, pendingProcessingRequest, submitProcessing],
  );

  const successMessage = useMemo(() => {
    if (!submissionResult) {
      return null;
    }
    const returnCount = submissionResult.returnBinIds.length;
    const disposeCount = submissionResult.disposeBinIds.length;
    const total = returnCount + disposeCount;

    if (total === 0) {
      return 'No bin items required processing.';
    }

    const detailSegments: string[] = [];
    if (returnCount > 0) {
      detailSegments.push(`${returnCount} return${returnCount === 1 ? '' : 's'}`);
    }
    if (disposeCount > 0) {
      detailSegments.push(`${disposeCount} disposal${disposeCount === 1 ? '' : 's'}`);
    }

    const detailLabel = detailSegments.length ? ` (${detailSegments.join(' • ')})` : '';
    let message = `Processed ${total} asset${total === 1 ? '' : 's'}${detailLabel}.`;

    const disabledCount = submissionResult.disabledAssetIds.length;
    if (disabledCount > 0) {
      message += ` Disabled ${disabledCount} asset${disabledCount === 1 ? '' : 's'} from stock.`;
    }

    return message;
  }, [submissionResult]);

  const processedAtDisplay = useMemo(() => {
    if (!submissionResult) {
      return null;
    }

    const processedAt = submissionResult.processedAt;
    const timestamp = processedAt ? new Date(processedAt) : null;

    if (!timestamp || Number.isNaN(timestamp.getTime())) {
      return null;
    }

    return timestamp.toLocaleString();
  }, [submissionResult]);

  const rows = useMemo<GridRow[]>(() => {
    return groups.map((group) => {
      return {
        id: group.key,
        assetName: group.assetName,
        assetLin: group.assetLin ?? '—',
        assetNsn: group.assetNsn ?? '—',
        binQty: group.recordIds.length,
        stagedQty: group.stagedRecordIds.length,
        serviceableCount: group.serviceableCount,
        nonServiceableCount: group.nonServiceableCount,
        recoverable: formatRecoverable(group.catalogRecoverable).label,
        catalogSize: group.catalogSize ?? '—',
        serviceType: getServiceSummary(group),
        returnedQty: group.stagedRecordIds.length,
      };
    });
  }, [groups]);

  const columns = useMemo<GridColDef<GridRow>[]>(() => {
    return [
      {
        field: 'assetName',
        headerName: 'Asset Name',
        flex: 1,
        minWidth: 220,
      },
      {
        field: 'catalogSize',
        headerName: 'Size',
        width: 120,
        filterable: false,
        renderCell: ({ row }) => <Typography variant="body2">{row.catalogSize}</Typography>,
      },
      {
        field: 'returnQty',
        headerName: 'Qty Selected',
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const key = row.id;
          const binQty = row.binQty;
          const group = groupsByKey.get(key);
          const existingSelection = selectionByGroup[key];
          const baseValue =
            inputQtyByGroup[key] ?? existingSelection?.quantity ?? Math.min(1, binQty);

          const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const raw = Number(event.target.value);
            const sanitized = Number.isNaN(raw) ? 0 : raw;
            const clampedToBin = Math.max(0, Math.min(binQty, sanitized));

            if (existingSelection && group) {
              const maxForSelection = group.recordIds.length;
              const adjusted = Math.max(0, Math.min(maxForSelection, clampedToBin));
              if (adjusted <= 0) {
                setSelectionByGroup((previous) => {
                  if (!previous[key]) {
                    return previous;
                  }
                  const { [key]: _removed, ...rest } = previous;
                  return rest;
                });
                setInputQtyByGroup((previous) => {
                  if (!(key in previous)) {
                    return previous;
                  }
                  const { [key]: _removed, ...rest } = previous;
                  return rest;
                });
              } else {
                setSelectionByGroup((previous) => {
                  const current = previous[key];
                  if (!current) {
                    return previous;
                  }
                  return { ...previous, [key]: { ...current, quantity: adjusted } };
                });
                setInputQtyByGroup((previous) => ({ ...previous, [key]: adjusted }));
              }
            } else {
              setInputQtyByGroup((previous) => ({ ...previous, [key]: clampedToBin }));
            }
            clearFeedback();
          };

          const qtyTooltip =
            row.binQty === 0
              ? 'No items available in this bin.'
              : '';

          return (
            <Tooltip title={qtyTooltip} arrow>
              <span>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 0, max: binQty, style: { textAlign: 'right' } }}
                  disabled={isViewer || row.binQty === 0 || isProcessing}
                  value={baseValue}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">/ {binQty}</InputAdornment>,
                  }}
                  sx={{ width: 110 }}
                />
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: 'returnedQty',
        headerName: 'Pending Return',
        width: 130,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ fontWeight: value > 0 ? 'bold' : 'normal' }}>
            {value}
          </Typography>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 340,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const key = row.id;
          const binQty = row.binQty;
          const group = groupsByKey.get(key);
          const isStaged = row.stagedQty > 0;

          const baseQty =
            inputQtyByGroup[key] ?? selectionByGroup[key]?.quantity ?? Math.min(1, binQty);

          const canReturn = Boolean(group?.recordIds.length);
          const canDispose = Boolean(group?.recordIds.length);
          const normalizedReturnQty = Math.max(0, Math.min(group?.recordIds.length || 0, baseQty));
          const normalizedDisposeQty = Math.max(0, Math.min(group?.recordIds.length || 0, baseQty));

          const selectedAction = selectionByGroup[key];
          const hasSelectedQuantity = Boolean(
            selectedAction?.quantity && selectedAction.quantity > 0,
          );
          const isReturnSelected =
            isStaged || (hasSelectedQuantity && selectedAction?.action === 'return');
          const isDisposeSelected = hasSelectedQuantity && selectedAction?.action === 'dispose';

          const handleDisposeSelect = () => {
            clearFeedback();
            const maxForSelection = group?.recordIds.length ?? 0;
            if (maxForSelection <= 0) {
              return;
            }

            const nextQuantity = getNextDisposeQuantity({
              currentSelection: selectedAction,
              requestedQuantity: normalizedDisposeQty,
              maxQuantity: maxForSelection,
            });

            if (nextQuantity <= 0) {
              return;
            }

            if (selectedAction?.action === 'dispose' && selectedAction.quantity === nextQuantity) {
              return;
            }

            setSelectionByGroup((previous) => ({
              ...previous,
              [key]: { action: 'dispose', quantity: nextQuantity },
            }));
            setInputQtyByGroup((previous) => ({ ...previous, [key]: nextQuantity }));
          };

          const isThisRowLoading =
            isProcessing &&
            processMutation.variables?.returnBinIds.some((id) => group?.recordIds.includes(id));

          const hasUnstagedItems = Boolean(group?.recordIds.length);

          const isReturnDisabled =
            isViewer ||
            !hasUnstagedItems ||
            !canReturn ||
            normalizedReturnQty <= 0 ||
            isProcessing;

          const returnTooltip = '';

          return (
            <Stack direction="row" spacing={1}>
              <Tooltip title={returnTooltip} arrow>
                <span>
                  <Button
                    variant={(isReturnSelected || isThisRowLoading) ? 'contained' : 'outlined'}
                    size="small"
                    color="primary"
                    sx={{ minWidth: 135 }}
                    onClick={async () => {
                      clearFeedback();
                      const maxForSelection = group?.recordIds.length ?? 0;
                      if (normalizedReturnQty > 0 && maxForSelection > 0) {
                        try {
                          await beginProcessing(
                            { [key]: { action: 'return', quantity: normalizedReturnQty } },
                            [key],
                          );
                        } catch (error) {
                          log.error('Failed to process return to shelf', { error: String(error) });
                          setSubmissionError(getErrorMessage(error));
                        }
                      }
                    }}
                    disabled={isReturnDisabled}
                    aria-disabled={isReturnDisabled}
                    aria-pressed={isReturnSelected}
                  >
                    {isThisRowLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      'Return to Shelf'
                    )}
                  </Button>
                </span>
              </Tooltip>
              {isStaged && !isViewer && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => handleCancelReturn(key)}
                  disabled={revertMutation.isPending}
                  aria-label={`Cancel pending return for ${row.assetName}`}
                >
                  Cancel Return
                </Button>
              )}
              {hasUnstagedItems && (
                <Button
                  variant={isDisposeSelected ? 'contained' : 'outlined'}
                  size="small"
                  color="warning"
                  onClick={handleDisposeSelect}
                  disabled={isViewer || !canDispose || normalizedDisposeQty <= 0 || isProcessing}
                  aria-pressed={isDisposeSelected}
                >
                  Dispose
                </Button>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'assetLin',
        headerName: 'LIN',
        width: 120,
        filterable: false,
        renderCell: ({ row }) => <Typography variant="body2">{row.assetLin}</Typography>,
      },
      {
        field: 'assetNsn',
        headerName: 'NSN',
        width: 200,
        filterable: false,
        renderCell: ({ row }) => <Typography variant="body2">{row.assetNsn}</Typography>,
      },
      {
        field: 'serviceType',
        headerName: 'Service Type',
        width: 350,
        filterable: false,
        renderCell: ({ row }) => {
          const group = groupsByKey.get(row.id);
          const hasBothTypes = row.serviceableCount > 0 && row.nonServiceableCount > 0;

          const handleEditClick = () => {
            if (group) {
              handleOpenEditModal(group);
            }
          };

          const displayedQty =
            inputQtyByGroup[row.id] ??
            selectionByGroup[row.id]?.quantity ??
            Math.min(1, row.binQty);
          const isEditDisabled = displayedQty === 0;

          const editButton = isViewer ? null : (
            <Tooltip
              title={isEditDisabled ? 'Select a quantity before editing the service type' : ''}
              disableHoverListener={!isEditDisabled}
              disableFocusListener={!isEditDisabled}
            >
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleEditClick}
                  disabled={isEditDisabled}
                  aria-label={`Edit service type for ${row.assetName}`}
                  sx={{ ml: 1 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          );

          if (hasBothTypes) {
            return (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Chip
                  label={`Serviceable (${row.serviceableCount})`}
                  color="success"
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Non-Serviceable (${row.nonServiceableCount})`}
                  color="warning"
                  size="small"
                />
                {editButton}
              </Stack>
            );
          }

          const normalized = row.serviceType.toLowerCase();
          const isServiceable = normalized === 'serviceable';
          const isNonServiceable = normalized.includes('non');
          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Chip
                label={row.serviceType}
                color={isServiceable ? 'success' : isNonServiceable ? 'warning' : 'default'}
                size="small"
                variant={isServiceable ? 'outlined' : 'filled'}
              />
              {editButton}
            </Stack>
          );
        },
      },
      {
        field: 'recoverable',
        headerName: 'Recoverable Type',
        width: 190,
        filterable: false,
        renderCell: ({ row }) => {
          const normalized = row.recoverable.toUpperCase();
          const color = normalized.includes('NON')
            ? 'warning'
            : normalized.includes('RECOVERABLE')
              ? 'success'
              : 'default';
          return <Chip label={row.recoverable} color={color} size="small" variant="outlined" />;
        },
      },
    ];
  }, [
    beginProcessing,
    clearFeedback,
    groupsByKey,
    handleCancelReturn,
    handleOpenEditModal,
    inputQtyByGroup,
    isProcessing,
    isViewer,
    processMutation.variables?.returnBinIds,
    revertMutation.isPending,
    selectionByGroup,
  ]);

  const hasDisposeItems = useMemo(
    () => Object.values(selectionByGroup).some((s) => s.action === 'dispose' && s.quantity > 0),
    [selectionByGroup],
  );

  const hasNonServiceableGroups = useMemo(
    () => groups.some((g) => g.nonServiceableCount > 0 && g.serviceableCount === 0 && g.recordIds.length > 0),
    [groups],
  );

  const hasServiceableGroups = useMemo(
    () => groups.some((g) => g.serviceableCount > 0 && g.recordIds.length > 0),
    [groups],
  );

  const disposeCartItems = useMemo<CartItem[]>(() => {
    return (Object.entries(selectionByGroup) as Array<[GroupKey, GroupSelection]>)
      .filter(([, selection]) => selection.action === 'dispose' && selection.quantity > 0)
      .map(([key, selection]) => {
        const group = groupsByKey.get(key);
        if (!group) return null;
        return {
          groupKey: key,
          assetName: group.assetName,
          assetLin: group.assetLin,
          assetNsn: group.assetNsn,
          quantity: selection.quantity,
          serviceSummary: getServiceSummary(group),
          recoverableLabel: formatRecoverable(group.catalogRecoverable).label,
        };
      })
      .filter((item): item is CartItem => Boolean(item));
  }, [groupsByKey, selectionByGroup]);

  if (isLoading) {
    return <LoadingSkeleton variant="grid" count={8} />;
  }

  return (
    <Container maxWidth={false} sx={{ mt: 3, mb: 4 }}>
      <TargetLocationSelectionModal
        open={isTargetLocationModalOpen}
        groups={targetLocationGroups}
        onCancel={handleTargetLocationCancel}
        onConfirm={handleTargetLocationConfirm}
      />
      <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
        Bulk Inventory Bin
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Review staged assets awaiting disposition. Return to Shelf processes immediately, while
        disposal selections stay queued until you complete them.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          Unable to load bulk inventory bin records. Please try again.
        </Alert>
      ) : null}

      <Grid container spacing={3} alignItems="flex-start">
        <Grid size={{ xs: 12, lg: isViewer ? 12 : 8 }}>
          <Stack spacing={2}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Typography variant="h6" component="h2">
                  Bulk Inventory Bin Assets
                </Typography>
                <Box sx={{ height: 540 }}>
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={isFetching}
                    density="comfortable"
                    disableRowSelectionOnClick
                    autoHeight={false}
                    getRowHeight={() => 'auto'}
                    sx={{
                      '& .MuiDataGrid-cell': {
                        display: 'flex',
                        alignItems: 'center',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        bgcolor: 'background.default',
                      },
                    }}
                  />
                </Box>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
        {!isViewer && (
        <Grid size={{ xs: 12, lg: 4 }}>
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                <Button
                  variant="outlined"
                  onClick={handleClearAll}
                  disabled={isProcessing || !hasDisposeItems}
                >
                  Clear All
                </Button>
                <Button
                  variant="contained"
                  color="info"
                  onClick={() => {
                    void handleProcessReturnToShelf();
                  }}
                  disabled={isProcessing || isFetching || !hasServiceableGroups}
                >
                  Process Return to Shelf
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => {
                    void handleProcessDisposal();
                  }}
                  disabled={isProcessing || isFetching || (!hasDisposeItems && !hasNonServiceableGroups)}
                >
                  Process Disposal
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    void handleProcessAll();
                  }}
                  disabled={isProcessing || isFetching || rows.length === 0}
                >
                  Process All
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" textAlign="right">
                Automatically routes serviceable items to Return to Shelf and non-serviceable items
                to Mark for Disposal.
              </Typography>
            </Stack>
            <CartSection
              title="Marked for Disposal"
              items={disposeCartItems}
              onRemove={handleCartRemove}
            />
            <Stack spacing={1.5}>
              {submissionError ? <Alert severity="error">{submissionError}</Alert> : null}
              {clearSuccessMessage ? (
                <Alert severity="info" onClose={() => setClearSuccessMessage(null)}>
                  {clearSuccessMessage}
                </Alert>
              ) : null}
              {successMessage ? (
                <Alert severity="success">
                  <Stack spacing={0.5}>
                    <span>{successMessage}</span>
                    {processedAtDisplay ? (
                      <Typography component="span" variant="caption" color="text.secondary">
                        Processed {processedAtDisplay}
                      </Typography>
                    ) : null}
                  </Stack>
                </Alert>
              ) : null}
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                onClick={handleComplete}
                disabled={!hasItemsToProcess || isProcessing}
                sx={{ height: 36.5 }} // Match standard button height to prevent jump
              >
                {isProcessing ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Complete'
                )}
              </Button>{' '}
              <Typography variant="caption" color="text.secondary" textAlign="center">
                Completes queued disposal and bulk actions. Items marked for disposal move to the
                Bulk Dispose screen for final processing.
              </Typography>
            </Stack>
          </Stack>
        </Grid>
        )}
      </Grid>

      {/* Cancel Return to Shelf Confirmation Dialog */}
      <Dialog
        open={Boolean(revertConfirmGroupKey)}
        onClose={() => setRevertConfirmGroupKey(null)}
        maxWidth="xs"
        aria-labelledby="revert-confirmation-title"
        aria-describedby="revert-confirmation-description"
      >
        <DialogTitle
          id="revert-confirmation-title"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <WarningIcon color="warning" />
          Cancel Return to Shelf
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="revert-confirmation-description">
            This will cancel the pending Return to Shelf action
            {revertConfirmGroupKey
              ? ` for ${groupsByKey.get(revertConfirmGroupKey)?.stagedRecordIds.length ?? 0} item${(groupsByKey.get(revertConfirmGroupKey)?.stagedRecordIds.length ?? 0) === 1 ? '' : 's'}`
              : ''}{' '}
            and return them to the available queue. They can then be re-routed to disposal if
            needed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertConfirmGroupKey(null)} color="inherit">
            Keep
          </Button>
          <Button
            onClick={() => {
              void handleRevertConfirmed();
            }}
            color="error"
            variant="contained"
          >
            Cancel Return
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All Confirmation Dialog */}
      <Dialog
        open={clearAllConfirmOpen}
        onClose={() => setClearAllConfirmOpen(false)}
        maxWidth="xs"
        aria-labelledby="clear-all-confirmation-title"
        aria-describedby="clear-all-confirmation-description"
      >
        <DialogTitle
          id="clear-all-confirmation-title"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <WarningIcon color="warning" />
          Clear All Items
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="clear-all-confirmation-description">
            Are you sure you want to clear all items? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearAllConfirmOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleClearAllConfirmed} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Service Type Modal */}
      <EditServiceTypeModal
        open={editModalOpen}
        assetName={editModalGroup?.assetName ?? ''}
        totalCount={editModalGroup?.recordIds.length ?? 0}
        initialServiceableCount={editModalGroup?.serviceableCount ?? 0}
        initialNonServiceableCount={editModalGroup?.nonServiceableCount ?? 0}
        serviceableBinIds={
          editModalGroup
            ? editModalGroup.recordIds.filter((id) => {
                const svc = (data.find((r) => r.id === id)?.serviceType ?? '').toLowerCase();
                return svc === 'serviceable';
              })
            : []
        }
        nonServiceableBinIds={
          editModalGroup
            ? editModalGroup.recordIds.filter((id) => {
                const svc = (data.find((r) => r.id === id)?.serviceType ?? '').toLowerCase();
                return svc !== 'serviceable';
              })
            : []
        }
        onClose={handleCloseEditModal}
      />
    </Container>
  );
};
